import { ReliningImageNameEnum } from '@trace/types';
import { UiSchemaCustom } from '../../../components/form-elements/JsonForms/types';

export const uiSchema: UiSchemaCustom = {
  metadata: {
    'ui:field': 'metadata',
    // GPS Coordinates is an optional header field rendered right below the
    // Address field (its position is controlled by the property order in the
    // template schema). It is shared across all installation types (Liner,
    // Spray, Branch and Other). The custom widget adds an "add automatically"
    // button that fills the field from the device location on demand.
    gpsCoordinates: {
      'ui:title': 'relining.gpsCoordinates.title',
      'ui:widget': 'gpsCoordinates',
    },
  },
  relinings: {
    'ui:title': '',
    'ui:options': {
      addable: false,
      removable: true,
      orderable: false,
    },
    items: {
      installation: {
        'ui:widget': 'radio',
      },
      installationOther: {
        'ui:widget': 'installationOther',
      },
      preparations: {
        'ui:title': 'relining.preparations.title',
        preparationType: {
          'ui:widget': 'checkboxes',
        },
        preparationTypeOther: {
          'ui:widget': 'other',
        },
        pipeTypeOther: {
          'ui:widget': 'other',
        },
        // Free-text field where the user manually enters the equipment used
        // during the installation. Rendered right below "Preparation type" for
        // every installation type (Liner, Spray, Branch and Other). Limited to
        // a maximum number of words (see EquipmentTypeWidget) with live
        // validation so the value stays readable on the certificate.
        equipmentType: {
          'ui:title': 'relining.equipmentType.title',
          'ui:widget': 'equipmentType',
        },
      },
      lining: {
        'ui:widget': 'checkboxes',
      },
      liningOther: {
        'ui:widget': 'other',
      },
      liningInstallationEquipment: {
        'ui:widget': 'textarea',
      },

      roller: {
        'ui:title': 'relining.roller.title',
        'ui:widget': 'text',
      },
      branchRoller: {
        'ui:title': 'relining.roller.title',
        'ui:widget': 'text',
      },

      eventDescription: {
        'ui:title': 'relining.eventDescription',
        preparationsDescription: {
          'ui:widget': 'textarea',
        },
        preparationsImage: {
          'ui:imageTitle': 'relining.preparationsDescriptionImageTitle',
          'ui:field': 'reliningImage',
          'ui:imageName': ReliningImageNameEnum.PREPARATIONS,
          'ui:buttonLabel': 'relining.preparations.imageTitle',
        },
        preparationsImageTwo: {
          'ui:imageTitle': 'relining.preparationsDescriptionImageTitle',
          'ui:field': 'reliningImage',
          'ui:imageName': ReliningImageNameEnum.PREPARATIONS_TWO,
          'ui:buttonLabel': 'relining.preparations.imageTitle',
        },
        preparationsImageThree: {
          'ui:imageTitle': 'relining.preparationsDescriptionImageTitle',
          'ui:field': 'reliningImage',
          'ui:imageName': ReliningImageNameEnum.PREPARATIONS_THREE,
          'ui:buttonLabel': 'relining.preparations.imageTitle',
        },
      },

      result: {
        'ui:widget': 'radio',
      },

      alternativeSprayRelining: {
        'ui:field': 'doubleBorderField',
        'ui:title': 'SPRAY RELINING',
        alternativeSprayReliningOther: {
          'ui:widget': 'other',
        },
      },

      alternativeBranchRelining: {
        'ui:field': 'doubleBorderField',
        'ui:title': 'relining.alternativeBranchRelining.title',
        dimensionMillimeters: {
          'ui:title': 'relining.dimensionMillimeters.title',
        },
        dimensionMillimetersOther: {
          'ui:widget': 'other',
          'ui:title': 'Grentyp',
          'ui:placeholder': 'relining.dimensionMillimetersOther',
        },
      },

      sprayRelining: {
        'ui:field': 'doubleBorderField',
        'ui:title': 'relining.sprayRelining',
        items: {
          'ui:widget': 'checkboxes',
          'ui:title': '',
        },
        sprayReliningOther: {
          'ui:widget': 'other',
        },
      },

      pipeTemperature: {
        'ui:title': 'relining.pipeTemperature.title',
        'ui:widget': 'text',
      },

      layers: {
        'ui:title': 'relining.layers.title',
        numberOfLayers: {
          'ui:widget': 'text',
        },
        layerType: {
          'ui:widget': 'checkboxes',
        },
      },

      calculator: {
        'ui:title': 'relining.calculator.title',
        'ui:field': 'calculator',
      },

      hardeningTimeEstimate: {
        'ui:field': 'timeEstimate',
      },

      batches: {
        'ui:title': 'relining.title.batchNumber',
        liner: {
          batchImage: {
            'ui:imageTitle': 'relining.liner.batchNumber',
            'ui:field': 'reliningImage',
            'ui:imageName': ReliningImageNameEnum.LINER,
            'ui:hideCaption': true,
          },
          batchNumber: {
            'ui:title': 'relining.liner.batchNumber',
            'ui:widget': 'outlinedText',
          },
          'ui:field': 'batch',
        },
        componentBase: {
          batchImage: {
            'ui:imageTitle': 'relining.componentBase.batchNumber',
            'ui:field': 'reliningImage',
            'ui:imageName': ReliningImageNameEnum.BASE,
            'ui:hideCaption': true,
          },
          batchNumber: {
            'ui:title': 'relining.componentBase.batchNumber',
            'ui:widget': 'outlinedText',
          },
          'ui:field': 'batch',
        },
        componentHardener: {
          batchImage: {
            'ui:imageTitle': 'relining.componentHardener.batchNumber',
            'ui:field': 'reliningImage',
            'ui:imageName': ReliningImageNameEnum.HARDENER,
            'ui:hideCaption': true,
          },
          batchNumber: {
            'ui:title': 'relining.componentHardener.batchNumber',
            'ui:widget': 'outlinedText',
          },
          'ui:field': 'batch',
        },
      },

      tightnessControl: {
        'ui:title': 'relining.tightnessControl.title',
        start: {
          'ui:field': 'reliningImage',
          'ui:imageName': ReliningImageNameEnum.TIGHTNESS_CONTROL_START,
          'ui:imageType': 'start',
          'ui:imageTitle': 'relining.tightnessControl.start.title',
        },
        end: {
          'ui:field': 'reliningImage',
          'ui:imageName': ReliningImageNameEnum.TIGHTNESS_CONTROL_END,
          'ui:imageType': 'end',
          'ui:imageTitle': 'relining.tightnessControl.end.title',
        },
      },
      visualControlComment: {
        'ui:widget': 'textarea',
      },

      inspection: {
        'ui:title': 'relining.inspection.title',
        start: {
          'ui:field': 'reliningImage',
          'ui:imageName': ReliningImageNameEnum.INSPECTION_START,
          'ui:imageType': 'start',
          'ui:imageTitle': 'relining.inspection.start.title',
        },
        end: {
          'ui:field': 'reliningImage',
          'ui:imageName': ReliningImageNameEnum.INSPECTION_END,
          'ui:imageType': 'end',
          'ui:imageTitle': 'relining.inspection.end.title',
        },
      },

      installationPressure: {
        'ui:field': 'installationPressure',
      },
      temperatureInPipe: {
        'ui:field': 'temperatureInPipeImage',
      },
      showUvHardening: {
        'ui:widget': 'checkbox',
      },
      uvHardeningSpeed: {
        'ui:widget': 'uvHardeningSpeed',
        'ui:title': 'relining.uvHardeningSpeed',
      },
      installationDocument: {
        'ui:field': 'reliningFile',
        'ui:title': 'relining.installationDocument.title',
      },
      machinePicture: {
        'ui:field': 'reliningMachine',
        'ui:title': 'relining.machinePicture.title',
      },
      hardening: {
        'ui:field': 'hardening',
      },

      openingBranches: {
        'ui:rawOptions': true,
      },

      describeDeviation: {
        'ui:widget': 'textarea',
      },

      other: {
        'ui:widget': 'textarea',
      },

      showHeatHardening: {
        'ui:widget': 'switch',
      },
      heatHardening: {
        'ui:field': 'heatHardening',
      },
      hardeningTime: {
        'ui:field': 'hardeningTimer',
      },

      __DEPENDENTS__: {
        calculator: [
          {
            field: 'installation',
            enum: ['liner'],
          },
        ],
        lining: [
          {
            field: 'installation',
            enum: ['liner'],
          },
        ],
        roller: [
          {
            field: 'installation',
            enum: ['liner'],
          },
        ],
        branchRoller: [
          {
            field: 'installation',
            enum: ['branch'],
          },
        ],
        'batches.liner': [
          {
            field: 'installation',
            enum: ['liner', 'branch'],
          },
        ],
        /*
         * "Spray time of day" is only relevant for the Spray installation type.
         * Gating it here hides the field for the Liner, Branch and Other forms
         * and strips its value from the submitted document for those types (see
         * getDependentProcessedFormData).
         * */
        'batches.sprayTimeOfDay': [
          {
            field: 'installation',
            enum: ['spray'],
          },
        ],
        temperatureInPipe: [
          {
            field: 'installation',
            enum: ['liner'],
          },
          { not: 'showUvHardening' },
        ],
        installationPressure: [
          {
            field: 'installation',
            enum: ['liner'],
          },
        ],
        hardening: [
          {
            field: 'installation',
            enum: ['liner', 'branch'],
          },
        ],
        showHeatHardening: [
          {
            field: 'installation',
            enum: ['liner', 'branch'],
          },
        ],
        hardeningTime: [
          {
            field: 'installation',
            enum: ['liner', 'branch'],
          },
          { not: 'showUvHardening' },
        ],
        openingBranches: [
          {
            field: 'installation',
            enum: ['liner'],
          },
        ],
        tightnessControl: [
          {
            field: 'installation',
            enum: ['liner'],
          },
        ],
        inspection: [
          {
            field: 'installation',
            enum: ['liner'],
          },
        ],
        alternativeSprayRelining: [
          {
            field: 'installation',
            enum: ['spray'],
          },
        ],
        alternativeBranchRelining: [
          {
            field: 'installation',
            enum: ['branch'],
          },
        ],
        sprayRelining: [
          {
            field: 'installation',
            enum: ['spray'],
          },
        ],
        layers: [
          {
            field: 'installation',
            enum: ['spray'],
          },
        ],
        describeDeviation: [
          {
            field: 'result',
            enum: ['deviation', 'approvedDeviation'],
          },
        ],
        'preparations.preparationTypeOther': [
          {
            field: 'preparations.preparationType',
            enum: ['other'],
          },
        ],
        'preparations.pipeTypeOther': [
          {
            field: 'preparations.pipeType',
            enum: ['other'],
          },
        ],
        liningOther: [
          {
            field: 'lining',
            enum: ['other'],
          },
        ],
        liningInstallationEquipment: [
          {
            field: 'lining',
            enum: ['installationEquipment'],
          },
        ],
        heatHardening: ['showHeatHardening'],
        /*
         * The "UV Hardening" section (toggle + speed), the "Upload File"
         * document and the "Upload Picture of the Machine" fields are not part
         * of the Spray workflow. They are hidden - and their data stripped on
         * submit (see getDependentProcessedFormData) - for the Spray
         * installation type, while remaining available for Liner, Branch and
         * Other. There is no "not in enum" condition, so the allowed types are
         * listed explicitly.
         * */
        showUvHardening: [
          {
            field: 'installation',
            enum: ['liner', 'branch', 'other'],
          },
        ],
        uvHardeningSpeed: [
          {
            field: 'installation',
            enum: ['liner', 'branch', 'other'],
          },
          'showUvHardening',
        ],
        installationDocument: [
          {
            field: 'installation',
            enum: ['liner', 'branch', 'other'],
          },
        ],
        machinePicture: [
          {
            field: 'installation',
            enum: ['liner', 'branch', 'other'],
          },
        ],
        /*
         * When UV Hardening is enabled these curing fields are irrelevant, so
         * they are hidden and their data is stripped from the submitted
         * document (see getDependentProcessedFormData).
         * */
        hardeningTimeEstimate: [{ not: 'showUvHardening' }],
        'alternativeSprayRelining.alternativeSprayReliningOther': [
          {
            field: 'alternativeSprayRelining.materials',
            enum: ['other'],
          },
        ],
        'alternativeBranchRelining.dimensionMillimetersOther': [
          {
            field: 'alternativeBranchRelining.dimensionMillimeters',
            enum: ['other'],
          },
        ],
        'sprayRelining.sprayReliningOther': [
          {
            field: 'sprayRelining.items',
            enum: ['other'],
          },
        ],

        'inspection.end': ['inspection.start.imageUrl'],
        'tightnessControl.end': ['tightnessControl.start.imageUrl'],

        'eventDescription.preparationsImageTwo': [
          { resolveBy: 'or' },
          'eventDescription.preparationsImage.imageUrl',
          'eventDescription.preparationsImageTwo.imageUrl',
        ],
        'eventDescription.preparationsImageThree': [
          { resolveBy: 'or' },
          'eventDescription.preparationsImageTwo.imageUrl',
          'eventDescription.preparationsImageThree.imageUrl',
        ],
      },
    },
  },
};
